import "../WebUrls/WebUrls.css";
import { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Card, CardContent } from "../../ui/card";
import { Globe, ShieldCheck } from "lucide-react";
import EmptyStateCard from "../EmptyStateCard";
import { WebUrlRecord } from "../WebUrls/interface";
import WebURLConnectAccess from "./WebURLConnectAccess";
import { myLocalStorage } from "../../StorageHelper";
import useNotificationsStore from "../../../services/notificationStore";
import moment from "moment";
import { NGROK } from "../../../APIs";
import axios from "axios";
import useUserStore from "../../../services/userStore";
import { v4 as uuidv4 } from "uuid";
import { useToast } from "../../../hooks/use-toast";

interface TenantWebUrlsTableProps {
  webUrls: WebUrlRecord[];
  totalCount?: number;
  onConnect?: (urlId: string) => void;
  refetchWebUrls?: () => void;
  searchQuery: string;
}

export default function TenantWebUrlsTable({
  webUrls,
  totalCount = 0,
  onConnect,
  refetchWebUrls,
  searchQuery = "",
}: TenantWebUrlsTableProps) {
  const [showAccessDialog, setShowAccessDialog] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState<WebUrlRecord | null>(null);
  const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
  const { userWebUrlApprovedNotifications } = useNotificationsStore();
  const userData = useUserStore((state) => state.user);
  const access_token = myLocalStorage.getItem("access_token");
  const { toast } = useToast();
  const refetchWebUrlsRef = useRef(refetchWebUrls);
  refetchWebUrlsRef.current = refetchWebUrls;

  let userAccess = userData.role;
  let loginemail = userData.email;

  const fetchResourceLevel = useMemo(
    () =>
      userWebUrlApprovedNotifications?.filter(
        (userWebUrlNotification: any) =>
          userWebUrlNotification.requestStatus === "APPROVED",
      ) ?? [],
    [userWebUrlApprovedNotifications],
  );

  const checkAccessTiming = (weburl: any) => {
    const record = fetchResourceLevel.find(
      (data: any) => data.accessUrl === weburl?.url,
    );

    if (!record) return false;

    const schedules = record.schedules;

    if (!schedules || schedules.length === 0) {
      if (
        record.accessTimeOverAt &&
        moment.utc().isAfter(moment.utc(record.accessTimeOverAt))
      ) {
        return false;
      }
      return record;
    }

    const nowUTC = moment.utc();

    const isActive = schedules.some((schedule: any) => {
      const scheduledStartTime = moment.utc(schedule.scheduledStartTime);
      const scheduledEndTime = moment.utc(schedule.scheduledEndTime);
      const isWithinSchedule = nowUTC.isBetween(
        scheduledStartTime,
        scheduledEndTime,
        null,
        "[]",
      );
      return isWithinSchedule;
    });

    return isActive;
  };

  const getFaviconUrl = (url: string) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?sz=64&domain=${domain}`;
    } catch {
      return undefined;
    }
  };

  const userloginToUrl = async (item: any) => {
    const filteredResourceUser = fetchResourceLevel.find(
      (user: any) => user.accessUrl === item?.url,
    );
    if (filteredResourceUser) {
      const isPremanentAccess = filteredResourceUser.privilegeLevel;
      const accessCredential = filteredResourceUser.accessCredential;
      const matchedUser = item?.credentialsList.find(
        (user: any) => user.username === accessCredential,
      );
      const result = matchedUser
        ? {
            email: matchedUser.username,
            authValutTags: matchedUser.id,
          }
        : null;
      let differenceInMilliseconds = 0;
      if (isPremanentAccess === "DYNAMIC") {
        const isUTC = filteredResourceUser.accessTimeOverAt.endsWith("Z");
        let dateTimeStr = "";
        if (!isUTC) {
          dateTimeStr = filteredResourceUser.accessTimeOverAt + "Z";
        }
        const dateTime = new Date(dateTimeStr);
        const now = new Date();
        let istOffset = 5.5 * 60;
        let pendingTime_ist: any = new Date(
          dateTime.getTime() + istOffset * 60 * 1000,
        );
        if (
          filteredResourceUser.schedules &&
          filteredResourceUser.schedules.length > 0
        ) {
          const schedule = filteredResourceUser.schedules[0];
          const utcDate = new Date(schedule.scheduledEndTime);
          const istOffsetMs = (5 * 60 + 30) * 60 * 1000;
          const istDate = new Date(utcDate.getTime() + istOffsetMs);
          pendingTime_ist = new Date(istDate.getTime() + istOffset * 60 * 1000);
        }

        let currentDate_ist: any = new Date(
          now.getTime() + istOffset * 60 * 1000,
        );
        differenceInMilliseconds = currentDate_ist - pendingTime_ist;
      }

      const { authValutTags, email } = result ?? {
        authValutTags: [],
        email: "",
      };
      let randomUUID = uuidv4();
      let payload = {
        tokenpasskey: randomUUID,
        userTag: authValutTags,
        email: email,
        token: access_token,
        url: NGROK,
        executeURL: item.url,
        role: isPremanentAccess === "PERMANENT" ? "" : userAccess,
        duration:
          isPremanentAccess === "PERMANENT"
            ? 0
            : Math.abs(differenceInMilliseconds),
        tenantName: tenantName,
        webId: item.id,
        loginemail: loginemail,
        isRecordingOff: !item?.recording,
      };
      await axios
        .get(`${NGROK}/api/websites/save-web-url-click?uuid=${randomUUID}`)
        .then(async () => {
          await axios
            .post("http://localhost:5051/start-automation", payload)
            .then((data) => {})
            .catch((e) => {
              const message = e?.response?.data?.message;
              toast({
                title: "Error",
                description: `${message} || WSWebApp not running... Make sure that the zip is downloaded and WSWebApp is running`,
                variant: "destructive",
              });
            });
        })
        .catch((e) => {
          const message = e?.response?.data?.message;
          toast({
            title: "Error",
            description: `${message} || WSWebApp not running... Make sure that the zip is downloaded and WSWebApp is running`,
            variant: "destructive",
          });
        });
    }
  };

  const handleConnect = (urlId: string) => {
    const url = webUrls.find((u) => u.id === Number(urlId));
    if (url) {
      setSelectedUrl(url);
      setShowAccessDialog(true);
    }
    onConnect?.(urlId);
  };

  const webUrlsMapped = useMemo(() => {
    const mapped = webUrls.map((webUrl) => ({
      id: String(webUrl.id),
      url: webUrl.url,
      domain: webUrl.tenantName,
      description: webUrl.isManaged ? "Managed by admin" : "Unmanaged URL",
      icon: "Globe",
      createdAt: webUrl.date,
      createdBy: webUrl.createdBy,
      credentialsList: webUrl.auth.map((auth) => ({
        id: auth.authValutTags,
        username: auth.email,
      })),
      credentials: webUrl.auth.length,
      management: {
        published: webUrl.publishedroleIds.length > 0,
        roles: webUrl.publishedroleIds.map((id) => String(id)),
      },
      recording: webUrl.recordingenabled,
    }));
    return mapped;
  }, [webUrls]);

  useEffect(() => {
    if (!refetchWebUrls) return;

    const interval = setInterval(() => {
      refetchWebUrlsRef.current?.();
    }, 10000);

    return () => clearInterval(interval);
  }, [refetchWebUrls == null]);
  return (
    <div className="space-y-4">
      {webUrlsMapped.length === 0 ? (
        <EmptyStateCard
          icon={Globe}
          title={
            searchQuery
              ? "No web URIs match your search"
              : "No web URIs available"
          }
          description={
            searchQuery
              ? "Try adjusting your search terms."
              : "No web URIs are currently assigned to your roles."
          }
        />
      ) : (
        <>
          <div className="web-url-assigned-header">
            <div className="flex items-center gap-3">
              <span className="web-urls-icon">
                <Globe aria-hidden="true" />
              </span>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                  Available Web URIs
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {totalCount || webUrlsMapped.length} web URI
                  {(totalCount || webUrlsMapped.length) !== 1 ? "s" : ""}{" "}
                  assigned to your roles
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {webUrlsMapped.map((webUrl) => (
              <Card key={webUrl.id} className="web-url-assigned-card">
                <CardContent className="p-6">
                  {}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="web-urls-icon">
                        {getFaviconUrl(webUrl.url) ? (
                          <img
                            src={getFaviconUrl(webUrl.url)}
                            alt="favicon"
                            className="w-6 h-6 object-contain"
                          />
                        ) : (
                          <Globe className="w-6 h-6 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4
                          className="web-url-assigned-name"
                          data-testid={`text-weburl-name-${webUrl.id}`}
                        >
                          {webUrl.url}
                        </h4>
                      </div>
                    </div>
                  </div>

                  {}
                  {webUrl.description && (
                    <div className="mb-4">
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {webUrl.description}
                      </p>
                    </div>
                  )}

                  {}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge variant="outline" className="web-url-chip">
                      {webUrl.credentials} credential
                      {webUrl.credentials !== 1 ? "s" : ""}
                    </Badge>
                    {webUrl.recording && (
                      <Badge variant="outline" className="web-url-chip" data-tone="primary">
                        Recording
                      </Badge>
                    )}
                  </div>

                  {}
                  <div className="pt-3 border-t border-border">
                    {checkAccessTiming(webUrl) ? (
                      <Button
                        size="sm"
                        onClick={() => userloginToUrl(webUrl)}
                        data-testid={`button-connect-${webUrl.id}`}
                        className="web-url-connect-button w-full gap-2"
                      >
                        <ShieldCheck className="w-4 h-4" />
                        Connect
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleConnect(webUrl.id)}
                        data-testid={`button-connect-${webUrl.id}`}
                        className="web-url-connect-button w-full gap-2"
                      >
                        <ShieldCheck className="w-4 h-4" />
                        Request Connect Access
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {showAccessDialog && selectedUrl && (
        <WebURLConnectAccess
          open={showAccessDialog}
          onOpenChange={() => setShowAccessDialog(false)}
          tenant={tenantName}
          webURL={selectedUrl}
        />
      )}
    </div>
  );
}
